import { createElement } from 'react';
import { Lock } from 'lucide-react';
import type { StatutEtape } from '@/shared/donnees/types';
import { dansLOrdre, estBloquee } from '@/shared/referentiel/chantier';
import { iconeEtape } from '@/shared/referentiel/icones';
import {
  etatChaine,
  FOND_ETAT_CHAINE,
  LIBELLE_ETAT_CHAINE,
  ORDRE_ETATS_CHAINE,
  type EtatChaine,
} from './chaine';
import { cn } from './cn';

interface EtapeDeChaine {
  id: string;
  libelle: string;
  ordre: number;
  finale: boolean;
  icone: string;
  statut: StatutEtape;
  enAttenteDe?: string | null;
}

/**
 * La chaîne d'étapes d'un chantier, en frise : une pastille par étape, dans
 * l'ordre, colorée selon son état. Le trait qui les relie se colore entre deux
 * étapes validées. Une pastille ouvre son étape.
 */
export function ChaineEtapes({
  etapes,
  onOuvrir,
  className,
}: {
  etapes: readonly EtapeDeChaine[];
  onOuvrir?: ((etapeId: string) => void) | undefined;
  className?: string;
}) {
  const ordonnees = dansLOrdre(etapes);
  const etats = ordonnees.map((e) => etatChaine(e.statut));
  const presents = new Set(etats);

  return (
    <div className={className}>
      <ol
        className="grid auto-cols-[minmax(76px,1fr)] grid-flow-col overflow-x-auto pb-1"
        aria-label="Chaîne des étapes"
      >
        {ordonnees.map((etape, i) => {
          const etat = etats[i] ?? 'a-faire';
          const lienAvant = i > 0 && etat === 'validee' && etats[i - 1] === 'validee';
          const lienApres =
            i < ordonnees.length - 1 && etat === 'validee' && etats[i + 1] === 'validee';
          const contenu = (
            <>
              <span className="relative flex h-9 items-center justify-center">
                {i > 0 ? (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-1/2 left-0 h-[3px] w-1/2 -translate-y-1/2',
                      lienAvant ? 'bg-chaine-validee' : 'bg-border'
                    )}
                  />
                ) : null}
                {i < ordonnees.length - 1 ? (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-1/2 right-0 h-[3px] w-1/2 -translate-y-1/2',
                      lienApres ? 'bg-chaine-validee' : 'bg-border'
                    )}
                  />
                ) : null}
                <Pastille etat={etat} icone={etape.icone} bloquee={estBloquee(etape)} />
              </span>
              <span
                className={cn(
                  'mt-1.5 line-clamp-2 px-1 text-center text-[11px] leading-tight',
                  etat === 'a-faire' ? 'text-muted' : 'text-ink'
                )}
              >
                {etape.libelle}
              </span>
            </>
          );
          const titre = `${etape.libelle} — ${LIBELLE_ETAT_CHAINE[etat]}${
            estBloquee(etape) && etape.enAttenteDe ? ` (attend : ${etape.enAttenteDe})` : ''
          }`;
          return (
            <li key={etape.id} className="min-w-0">
              {onOuvrir ? (
                <button
                  type="button"
                  title={titre}
                  aria-label={titre}
                  onClick={() => {
                    onOuvrir(etape.id);
                  }}
                  className="group flex w-full flex-col items-center rounded-lg py-1 hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-primary"
                >
                  {contenu}
                </button>
              ) : (
                <span title={titre} className="flex flex-col items-center py-1">
                  {contenu}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted">
        {ORDRE_ETATS_CHAINE.filter((e) => presents.has(e)).map((e) => (
          <li key={e} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={cn('size-2.5 rounded-full', FOND_ETAT_CHAINE[e])} />
            {LIBELLE_ETAT_CHAINE[e]}
          </li>
        ))}
        {ordonnees.some(estBloquee) ? (
          <li className="inline-flex items-center gap-1.5">
            <Lock className="size-3" aria-hidden />
            Attend une autre étape
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function Pastille({ etat, icone, bloquee }: { etat: EtatChaine; icone: string; bloquee: boolean }) {
  const vide = etat === 'a-faire';
  return (
    <span
      className={cn(
        'relative grid size-9 place-items-center rounded-full',
        vide
          ? 'border-2 border-chaine-a-faire bg-surface text-muted'
          : cn(FOND_ETAT_CHAINE[etat], etat === 'commencee' ? 'text-info' : 'text-white')
      )}
    >
      {/* createElement : l'icône vient d'un nom rangé en base, pas d'un composant déclaré. */}
      {createElement(iconeEtape(icone), { className: 'size-[18px]', 'aria-hidden': true })}
      {bloquee ? (
        <span className="absolute -right-1 -bottom-1 grid size-4 place-items-center rounded-full border border-border bg-surface text-muted">
          <Lock className="size-2.5" aria-hidden />
        </span>
      ) : null}
    </span>
  );
}
